import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { estimateFinalStepSchema, EstimateFinalStepValues } from '../types/estimateFormTypes';
import { useEstimateVersioningEnhanced } from './useEstimateVersioningEnhanced';
import { LocalLineItem } from '../line-items/hooks/useLocalLineItems';
import { AdjustmentSettings } from '@/types/adjustment-filters';

// Interface for pending changes passed to atomic version creation
interface PendingChanges {
  localLineItems: LocalLineItem[];
  baselineSnapshot: LocalLineItem[];
  adjustmentSettings: AdjustmentSettings;
  finalStepFields: {
    special_instructions: string;
    payment_terms: string;
    validity_period: number;
  };
}

export const useEstimateFinalStep = (estimateId: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [estimateStatus, setEstimateStatus] = useState<'draft' | 'sent' | 'approved' | 'rejected' | 'expired' | 'cancelled'>('draft');
  const [originalSnapshot, setOriginalSnapshot] = useState<{
    special_instructions: string;
    payment_terms: string;
    validity_period: number;
  } | null>(null);
  const [defaultValidityDays, setDefaultValidityDays] = useState<number>(14);
  
  const queryClient = useQueryClient();

  const {
    createVersionEnhanced,
    sendVersion,
    isLoading: isCreatingVersion
  } = useEstimateVersioningEnhanced();

  const form = useForm<EstimateFinalStepValues>({
    resolver: zodResolver(estimateFinalStepSchema),
    defaultValues: {
      special_instructions: '',
      payment_terms: '',
      validity_period: 14,
    },
  });

  // Fetch existing estimate data and document defaults for validity_period fallback
  useEffect(() => {
    const fetchEstimate = async () => {
      if (!estimateId) return;
      
      setIsInitialLoading(true);
      try {
        const { data, error } = await supabase
          .from('estimates')
          .select('special_instructions, payment_terms, validity_period, status, company_id')
          .eq('id', estimateId)
          .single();

        if (error) throw error;

        // Fetch document defaults for validity_period fallback
        let docDefaultValidity = 14;
        if (data.company_id) {
          const { data: docDefaults } = await supabase
            .from('document_defaults')
            .select('estimate_validity_days')
            .eq('company_id', data.company_id)
            .maybeSingle();
          
          if (docDefaults?.estimate_validity_days) {
            docDefaultValidity = docDefaults.estimate_validity_days;
            setDefaultValidityDays(docDefaultValidity);
          }
        }

        setEstimateStatus(data.status as 'draft' | 'sent' | 'approved' | 'rejected' | 'expired' | 'cancelled');

        const snapshotData = {
          special_instructions: data.special_instructions || '',
          payment_terms: data.payment_terms || '',
          validity_period: data.validity_period || docDefaultValidity,
        };

        form.reset(snapshotData);
        
        // Store original snapshot for change detection
        setOriginalSnapshot(snapshotData);
      } catch (error) {
        console.error('Error fetching estimate final step data:', error);
        toast.error('Failed to load estimate details');
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchEstimate();
  }, [estimateId, form]);

  const handleSubmit = async (
    data: EstimateFinalStepValues, 
    onSuccess?: () => void,
    onVersionCreated?: (versionId: string, versionType: 'estimate' | 'change_order', versionNumber: number) => void
  ) => {
    // Check if there are actual changes for sent/approved estimates
    const hasChanges = originalSnapshot && (
      data.special_instructions !== originalSnapshot.special_instructions ||
      data.payment_terms !== originalSnapshot.payment_terms ||
      data.validity_period !== originalSnapshot.validity_period
    );

    // Check if versioning is required based on current status AND changes
    // Sent, Rejected, Expired → Create new version
    if (['sent', 'rejected', 'expired'].includes(estimateStatus) && hasChanges) {
      return { 
        needsVersioning: true, 
        warningType: 'new_version' as const,
        message: `This estimate is ${estimateStatus}. Completing with changes will create a new version.`
      };
    }
    
    // Approved → Create change order
    if (estimateStatus === 'approved' && hasChanges) {
      return { 
        needsVersioning: true, 
        warningType: 'change_order' as const,
        message: 'This estimate has been approved by the client. Completing with changes will create a change order that maintains links to existing invoices and payments.'
      };
    }

    // Only for draft status - proceed with direct update
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('estimates')
        .update({
          special_instructions: data.special_instructions || null,
          payment_terms: data.payment_terms || null,
          validity_period: data.validity_period,
          expires_at: new Date(Date.now() + (data.validity_period * 24 * 60 * 60 * 1000)).toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', estimateId);

      if (error) throw error;

      toast.success('Estimate details updated successfully');
      onSuccess?.();
      return { needsVersioning: false };
      
    } catch (error) {
      console.error('Error updating estimate final step:', error);
      toast.error('Failed to update estimate details');
      return { needsVersioning: false };
    } finally {
      setIsLoading(false);
    }
  };

  // COPY-ON-WRITE VERSION CREATION: For sent/approved estimates, changes go directly
  // to the new version's snapshot without modifying the shared estimate_line_items table.
  // This ensures version immutability - approved versions can never be changed.
  const createVersionAndComplete = async (
    data: EstimateFinalStepValues,
    versionType: 'estimate' | 'change_order',
    reason?: string,
    description?: string,
    onVersionCreated?: (versionId: string, versionType: 'estimate' | 'change_order', versionNumber: number) => void,
    pendingChanges?: PendingChanges
  ) => {
    setIsLoading(true);
    
    try {
      let lineItemsOverride: any[] | undefined;
      
      if (pendingChanges) {
        console.log('[useEstimateFinalStep] Copy-on-write version creation - building snapshot from local changes');
        
        const { localLineItems, adjustmentSettings, finalStepFields } = pendingChanges;
        
        // Build the line items snapshot from local state (excluding deleted items)
        // These will be passed directly to create_estimate_version_enhanced
        // instead of writing to estimate_line_items first
        lineItemsOverride = localLineItems
          .filter(li => !li._isDeleted)
          .map((item, index) => ({
            // Generate new ID for new items, keep existing ID for existing items
            id: item._isNew ? crypto.randomUUID() : item.id,
            component_id: item.component_id,
            component_type: item.component_type,
            description: item.description,
            quantity_in_base_units: item.quantity_in_base_units,
            quantity_in_productivity_units: null, // Not tracked in LocalLineItem
            base_unit: item.base_unit,
            unit_cost_snapshot: item.unit_cost_snapshot,
            total_cost_snapshot: item.total_cost_snapshot,
            manual_unit_cost: item.manual_unit_cost || null,
            manual_total_cost: item.manual_total_cost || null,
            manual_override: item.manual_override || false,
            sort_order: index,
            option_group_id: item.option_group_id || null,
            category_id: item.category_id || null,
            manual_description: item.manual_description || null,
            original_description: item.original_description || null,
          }));
        
        console.log('[useEstimateFinalStep] Built line items override:', {
          count: lineItemsOverride.length,
          items: lineItemsOverride.map(li => ({ id: li.id, desc: li.description?.substring(0, 30) }))
        });

        // Update estimate metadata (adjustment settings) - this is safe as it's not version-specific
        // The version's estimate_snapshot will capture these values
        const { error: estimateError } = await supabase
          .from('estimates')
          .update({
            special_instructions: finalStepFields.special_instructions || null,
            payment_terms: finalStepFields.payment_terms || null,
            validity_period: finalStepFields.validity_period,
            expires_at: new Date(Date.now() + (finalStepFields.validity_period * 24 * 60 * 60 * 1000)).toISOString(),
            adjustment_metadata: adjustmentSettings as any,
            updated_at: new Date().toISOString(),
          })
          .eq('id', estimateId);

        if (estimateError) throw estimateError;
      }

      // Create the version - pass line items as override for sent/approved estimates
      // The DB function will use these directly instead of reading from estimate_line_items
      console.log('[useEstimateFinalStep] Creating version with line items override');
      const versionResponse = await createVersionEnhanced(
        estimateId, 
        {}, 
        reason, 
        description,
        lineItemsOverride // Pass the modified line items directly
      );
      
      console.log('[useEstimateFinalStep] Version response:', JSON.stringify(versionResponse));
      
      // Ensure we have a valid version_id before calling the callback
      const versionId = versionResponse?.version_id;
      if (!versionId) {
        console.error('[useEstimateFinalStep] No version_id in response:', versionResponse);
        throw new Error('Failed to get version ID from response');
      }
      
      onVersionCreated?.(versionId, versionResponse.version_type, versionResponse.version_number);
      
      // Invalidate queries to refresh UI immediately
      // The sync_line_items_on_main_version_change trigger will update estimate_line_items
      // to match the new version's snapshot when main_version_id changes
      queryClient.invalidateQueries({ queryKey: ['estimateVersions', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimateLineItems', estimateId] });
      
      toast.success(`${versionType === 'change_order' ? 'Change order' : 'New version'} created successfully`);
      
      return versionResponse;
      
    } catch (error) {
      console.error('Error creating version:', error);
      toast.error('Failed to create version');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    form,
    isLoading: isLoading || isCreatingVersion,
    isInitialLoading,
    estimateStatus,
    handleSubmit,
    createVersionAndComplete,
    sendVersion,
  };
};
